import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('emims_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/login')) {
      localStorage.removeItem('emims_token');
      localStorage.removeItem('emims_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
};

export const policiesAPI = {
  list: () => api.get('/policies'),
  get: (id) => api.get(`/policies/${id}`),
  create: (data) => api.post('/policies', data),
  update: (id, data) => api.put(`/policies/${id}`, data),
  providers: () => api.get('/policies/providers'),
};

export const policyDocumentsAPI = {
  list: (policyId) => api.get(`/policies/${policyId}/documents`),
  upload: (policyId, formData) => api.post(`/policies/${policyId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  download: (policyId, docId) => api.get(`/policies/${policyId}/documents/${docId}/download`, { responseType: 'blob' }),
  remove: (policyId, docId) => api.delete(`/policies/${policyId}/documents/${docId}`),
};

export const slabsAPI = {
  byPolicy: (policyId) => api.get(`/slabs/policy/${policyId}`),
  create: (data) => api.post('/slabs', data),
  update: (id, data) => api.put(`/slabs/${id}`, data),
};

export const employeesAPI = {
  list: () => api.get('/employees'),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  lookups: () => api.get('/employees/lookups'),
  calculatePremium: (data) => api.post('/employees/calculate-premium', data),
  calculateExit: (id, data) => api.post(`/employees/${id}/calculate-exit`, data),
  extendFamilyWindow: (id, days = 30, reason) => api.patch(`/employees/${id}/extend-family-window`, { days, reason }),
  suspend: (id) => api.patch(`/employees/${id}/suspend`),
  exit: (id, data) => api.patch(`/employees/${id}/exit`, data),
  collectPremium: (id, data) => api.patch(`/employees/${id}/collect-premium`, data),
};

export const familyAPI = {
  byEmployee: (employeeId) => api.get(`/family/employee/${employeeId}`),
  calculatePremium: (data) => api.post('/family/calculate-premium', data),
  create: (data) => api.post('/family', data),
  remove: (id, data) => api.delete(`/family/${id}`, { data }),
};

export const claimsAPI = {
  list: () => api.get('/claims'),
  get: (id) => api.get(`/claims/${id}`),
  create: (data) => api.post('/claims', data),
  updateStatus: (id, data) => api.patch(`/claims/${id}/status`, data),
  uploadDocument: (id, formData) => api.post(`/claims/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const dashboardAPI = {
  metrics: () => api.get('/dashboard/metrics'),
  enrollmentReport: () => api.get('/dashboard/reports/enrollment'),
  claimsAging: () => api.get('/dashboard/reports/claims-aging'),
  premiumSummary: () => api.get('/dashboard/reports/premium-summary'),
  renewalDue: () => api.get('/dashboard/reports/renewal-due'),
  endorsements: () => api.get('/dashboard/reports/endorsements'),
  dependentAgeCompliance: () => api.get('/dashboard/reports/dependent-age-compliance'),
  claimStatus: () => api.get('/dashboard/reports/claim-status'),
  insurerReconciliation: () => api.get('/dashboard/reports/insurer-reconciliation'),
  premiumVsClaims: () => api.get('/dashboard/reports/premium-vs-claims'),
};

export const endorsementsAPI = {
  list: () => api.get('/endorsements'),
  updateStatus: (id, status) => api.patch(`/endorsements/${id}/status`, { status }),
  downloadPdf: (id) => api.get(`/endorsements/${id}/pdf`, { responseType: 'blob' }),
};

export const selfAPI = {
  summary: () => api.get('/self/summary'),
  profile: () => api.get('/self/profile'),
  policy: () => api.get('/self/policy'),
  policyDocuments: () => api.get('/self/policy/documents'),
  downloadPolicyDocument: (id) => api.get(`/self/policy/documents/${id}/download`, { responseType: 'blob' }),
  previewFamilyPremium: (data) => api.post('/self/family/preview-premium', data),
  uploadFamilyDocument: (formData) => api.post('/self/family/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  submitFamilyRequest: (data, idempotencyKey) => api.post('/self/family/requests', data, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
  }),
  resubmitFamilyRequest: (id, data, idempotencyKey) => api.post(`/self/family/requests/${id}/resubmit`, data, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
  }),
  familyRequests: () => api.get('/self/family/requests'),
  getFamilyRequest: (id) => api.get(`/self/family/requests/${id}`),
  cancelFamilyRequest: (id) => api.post(`/self/family/requests/${id}/cancel`),
  downloadEcard: () => api.get('/self/ecard', { responseType: 'blob' }),
  downloadEcardQr: () => api.get('/self/ecard/qr', { responseType: 'blob' }),
  searchHospitals: (params) => api.get('/self/hospitals', { params }),
  profileCorrectionRequests: () => api.get('/self/profile/correction-requests'),
  submitProfileCorrection: (data) => api.post('/self/profile/correction-requests', data),
  cancelProfileCorrection: (id) => api.post(`/self/profile/correction-requests/${id}/cancel`),
  claims: () => api.get('/self/claims'),
  getClaim: (id) => api.get(`/self/claims/${id}`),
  createClaim: (data) => api.post('/self/claims', data),
  uploadDocument: (id, formData) => api.post(`/self/claims/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const notificationsAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
};

export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

export const employeePortalUsersAPI = {
  list: () => api.get('/employee-portal-users'),
  create: (data) => api.post('/employee-portal-users', data),
  resetPassword: (id, password) => api.patch(`/employee-portal-users/${id}/reset-password`, { password }),
  setStatus: (id, isActive) => api.patch(`/employee-portal-users/${id}/status`, { is_active: isActive }),
};

export const approvalsAPI = {
  list: (params) => api.get('/approvals', { params }),
  get: (id) => api.get(`/approvals/${id}`),
  approve: (id) => api.post(`/approvals/${id}/approve`),
  reject: (id, data) => api.post(`/approvals/${id}/reject`, data),
  downloadDocument: (id) => api.get(`/approvals/documents/${id}/download`, { responseType: 'blob' }),
};

export const profileCorrectionsAPI = {
  list: (params) => api.get('/profile-corrections', { params }),
  approve: (id, data) => api.post(`/profile-corrections/${id}/approve`, data),
  reject: (id, data) => api.post(`/profile-corrections/${id}/reject`, data),
};

export const termCoveragesAPI = {
  list: () => api.get('/term-coverages'),
  byEmployee: (employeeId) => api.get(`/term-coverages/employee/${employeeId}`),
  calculatePremium: (data) => api.post('/term-coverages/calculate-premium', data),
  create: (data) => api.post('/term-coverages', data),
  collectPremium: (id, data) => api.patch(`/term-coverages/${id}/collect-premium`, data),
};

export const termClaimsAPI = {
  list: () => api.get('/term-claims'),
  get: (id) => api.get(`/term-claims/${id}`),
  create: (data) => api.post('/term-claims', data),
  updateStatus: (id, data) => api.patch(`/term-claims/${id}/status`, data),
  uploadDocument: (id, formData) => api.post(`/term-claims/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const hospitalsAPI = {
  list: (params) => api.get('/hospitals', { params }),
  get: (id) => api.get(`/hospitals/${id}`),
  create: (data) => api.post('/hospitals', data),
  update: (id, data) => api.put(`/hospitals/${id}`, data),
  remove: (id) => api.delete(`/hospitals/${id}`),
};

export const hrmsAPI = {
  config: () => api.get('/integrations/hrms/config'),
  sync: (data) => api.post('/integrations/hrms/sync', data || {}),
  syncStatus: () => api.get('/integrations/hrms/sync-status'),
  createDeduction: (data) => api.post('/integrations/hrms/deductions', data),
  getDeduction: (id, refresh = false) => api.get(`/integrations/hrms/deductions/${id}`, {
    params: refresh ? { refresh: true } : undefined,
  }),
};

export default api;
